import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { api } from "@/lib/api";

type Industry = { id: number; code: string; name: string };
type Shop = { id: number; code: string; name: string };

type PosOrderRow = {
  id: number;
  pos_order_id: number;
  pos_display_id: number | null;
  shop_id: number;
  industry_id: number | null;
  status: number;
  status_name: string | null;
  bill_full_name: string | null;
  bill_phone_number: string | null;
  total_price: string | null;
  cod: string | null;
  marketplace_id: string | null;
  received_at_shop: boolean | null;
  received_at: string | null;
  received_date: string | null;
  partner_id: number | null;
  partner_name: string | null;
  partner_extend_code: string | null;
  partner_order_number_vtp: string | null;
  partner_cod: string | null;
  partner_paid_at: string | null;
  partner_picked_up_at: string | null;
};

type DayStat = { date: string; orders: number; cod: number };

type ListResp = {
  items: PosOrderRow[];
  total: number;
  total_cod: number;
  by_day: DayStat[];
  page: number;
  page_size: number;
};

function fmtVnd(s: string | null): string {
  if (!s) return "—";
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString("vi-VN") + " đ";
}

function fmtDate(s: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" });
}

export function PosOrdersPage() {
  const qc = useQueryClient();
  const [shopId, setShopId] = useState<number | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);

  const shopsQ = useQuery({
    queryKey: ["shops"],
    queryFn: async () => (await api.get<Shop[]>("/shops")).data,
  });
  const industriesQ = useQuery({
    queryKey: ["industries"],
    queryFn: async () => (await api.get<Industry[]>("/industries")).data,
  });

  const params = useMemo(() => {
    const p: Record<string, string | number> = { page, page_size: 50, status: 3 };
    if (shopId) p.shop_id = shopId as number;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [shopId, dateFrom, dateTo, search, page]);

  const listQ = useQuery({
    queryKey: ["pos_orders", params],
    queryFn: async () => (await api.get<ListResp>("/pos-orders", { params })).data,
    placeholderData: (prev) => prev,
  });

  const totalCod = listQ.data?.total_cod ?? 0;

  const syncMut = useMutation({
    mutationFn: async (sId: number) =>
      (await api.post(`/pos-orders/sync/${sId}`)).data as { status: string; upserted: number },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pos_orders"] }),
  });

  const detailQ = useQuery({
    queryKey: ["pos_order", openId],
    queryFn: async () => (await api.get(`/pos-orders/${openId}`)).data,
    enabled: openId !== null,
  });

  const industryMap = useMemo(() => {
    const m: Record<number, string> = {};
    (industriesQ.data || []).forEach((i) => (m[i.id] = i.name));
    return m;
  }, [industriesQ.data]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            Đơn hàng POScake
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Chỉ đơn "Đã nhận" (status=3) — nguồn dữ liệu doanh thu chính.
          </p>
        </div>
        <button
          disabled={!shopId || syncMut.isPending}
          onClick={() => shopId && syncMut.mutate(shopId as number)}
          className="btn-primary"
        >
          {syncMut.isPending ? "↻ Đang đồng bộ..." : "↻ Đồng bộ ngay"}
        </button>
      </div>

      {syncMut.data && (
        <div className="mb-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-lg">
          ✓ Đồng bộ xong: {syncMut.data.upserted} đơn upsert.
        </div>
      )}

      <div className="card p-4 mb-4 flex gap-3 flex-wrap items-end">
        <div>
          <label className="label">Cửa hàng</label>
          <select
            value={shopId}
            onChange={(e) => {
              setShopId(e.target.value ? Number(e.target.value) : "");
              setPage(1);
            }}
            className="input min-w-[180px]"
          >
            <option value="">— Tất cả —</option>
            {(shopsQ.data || []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Từ ngày</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="input w-40"
          />
        </div>
        <div>
          <label className="label">Đến ngày</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="input w-40"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="label">Tìm (SĐT / tên khách)</label>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="0901..."
            className="input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="card p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            Tổng đơn
          </div>
          <div className="text-xl font-semibold text-slate-900 font-mono mt-1">
            {listQ.data?.total?.toLocaleString("vi-VN") ?? "—"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">theo bộ lọc</div>
        </div>
        <div className="card p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            Tổng COD
          </div>
          <div className="text-xl font-semibold text-emerald-700 font-mono mt-1">
            {totalCod.toLocaleString("vi-VN")}đ
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            tất cả trang · theo lọc
          </div>
        </div>
        <div className="card p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            Trang hiện tại
          </div>
          <div className="text-xl font-semibold text-slate-900 font-mono mt-1">
            {listQ.data?.items.length ?? 0} / {listQ.data?.page_size ?? 50}
          </div>
        </div>
      </div>

      {listQ.data && listQ.data.by_day.length > 0 && (
        <div className="card mb-4 overflow-hidden">
          <div className="px-4 py-2.5 text-sm font-semibold border-b border-slate-100 bg-slate-50 text-slate-700">
            Tổng COD theo ngày
          </div>
          <div className="overflow-x-auto max-h-72 scrollbar-thin">
            <table className="table-modern">
              <thead className="sticky top-0">
                <tr>
                  <th>Ngày</th>
                  <th className="text-right">Đơn</th>
                  <th className="text-right">COD</th>
                </tr>
              </thead>
              <tbody>
                {listQ.data.by_day.map((d) => (
                  <tr key={d.date}>
                    <td>
                      {new Date(d.date + "T00:00:00").toLocaleDateString(
                        "vi-VN"
                      )}
                    </td>
                    <td className="text-right font-mono">{d.orders}</td>
                    <td className="text-right font-mono font-semibold">
                      {d.cod.toLocaleString("vi-VN")}đ
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {listQ.isLoading && (
        <div className="text-slate-500 py-8 text-center">Đang tải...</div>
      )}
      {listQ.data && (
        <>
          <div className="card overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Đơn POS</th>
                  <th>Khách</th>
                  <th className="text-right">COD</th>
                  <th>ĐVVC</th>
                  <th>Mã vận đơn</th>
                  <th>Ngành</th>
                  <th>Nhận lúc</th>
                </tr>
              </thead>
              <tbody>
                {listQ.data.items.map((r) => (
                  <tr
                    key={r.id}
                    className="cursor-pointer"
                    onClick={() => setOpenId(r.id)}
                  >
                    <td className="font-mono text-xs">{r.pos_order_id}</td>
                    <td>
                      <div className="font-medium">
                        {r.bill_full_name || "—"}
                      </div>
                      <div className="text-xs text-slate-500 font-mono">
                        {r.bill_phone_number || ""}
                      </div>
                    </td>
                    <td className="text-right font-mono font-semibold">
                      {fmtVnd(r.cod)}
                    </td>
                    <td>
                      <span className="badge-neutral">
                        {r.partner_name || "—"}
                      </span>
                    </td>
                    <td className="font-mono text-xs text-slate-600">
                      {r.partner_extend_code || "—"}
                    </td>
                    <td className="text-xs text-slate-600">
                      {r.industry_id ? industryMap[r.industry_id] : "—"}
                    </td>
                    <td className="text-xs text-slate-500 font-mono">
                      {fmtDate(r.received_at)}
                    </td>
                  </tr>
                ))}
                {listQ.data.items.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center text-slate-500 py-8"
                    >
                      Không có đơn nào khớp bộ lọc.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 text-sm">
            <div className="text-gray-500">
              Trang {listQ.data.page} / {Math.max(1, Math.ceil(listQ.data.total / listQ.data.page_size))} —{" "}
              {listQ.data.total} đơn
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={listQ.data.page <= 1}
                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40"
              >
                ‹
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={listQ.data.page * listQ.data.page_size >= listQ.data.total}
                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40"
              >
                ›
              </button>
            </div>
          </div>
        </>
      )}

      {openId !== null && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpenId(null)}>
          <div
            className="bg-white w-[640px] h-full overflow-y-auto p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Chi tiết đơn #{detailQ.data?.pos_order_id}</h2>
              <button onClick={() => setOpenId(null)} className="text-gray-500 hover:text-gray-800">
                ✕
              </button>
            </div>
            {detailQ.isLoading && <div className="text-gray-500">Đang tải...</div>}
            {detailQ.data && (
              <div className="space-y-4 text-sm">
                <Section title="Khách hàng">
                  <Field label="Họ tên" value={detailQ.data.bill_full_name} />
                  <Field label="SĐT" value={detailQ.data.bill_phone_number} />
                </Section>
                <Section title="Tiền">
                  <Field label="Tổng hàng" value={fmtVnd(detailQ.data.total_price)} />
                  <Field label="COD" value={fmtVnd(detailQ.data.cod)} />
                </Section>
                <Section title="Vận chuyển">
                  <Field label="Đơn vị" value={detailQ.data.partner_name} />
                  <Field label="Mã vận đơn" value={detailQ.data.partner_extend_code} mono />
                  <Field label="VTP order_number" value={detailQ.data.partner_order_number_vtp} mono />
                  <Field label="ĐVVC COD" value={fmtVnd(detailQ.data.partner_cod)} />
                  <Field label="Lấy hàng lúc" value={fmtDate(detailQ.data.partner_picked_up_at)} />
                  <Field label="Trả tiền lúc" value={fmtDate(detailQ.data.partner_paid_at)} />
                </Section>
                <details className="bg-gray-50 rounded p-2 border border-gray-200">
                  <summary className="cursor-pointer text-xs text-gray-600">
                    Raw partner timeline (extend_update)
                  </summary>
                  <pre className="text-xs overflow-x-auto mt-2">
                    {JSON.stringify(detailQ.data.partner_json?.extend_update || [], null, 2)}
                  </pre>
                </details>
                <details className="bg-gray-50 rounded p-2 border border-gray-200">
                  <summary className="cursor-pointer text-xs text-gray-600">
                    Items ({detailQ.data.items_json?.length || 0})
                  </summary>
                  <pre className="text-xs overflow-x-auto mt-2">
                    {JSON.stringify(detailQ.data.items_json || [], null, 2)}
                  </pre>
                </details>
                <details className="bg-gray-50 rounded p-2 border border-gray-200">
                  <summary className="cursor-pointer text-xs text-gray-600">Raw payload</summary>
                  <pre className="text-xs overflow-x-auto mt-2">
                    {JSON.stringify(detailQ.data.raw_data || {}, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: any; mono?: boolean }) {
  return (
    <div className="flex">
      <div className="w-32 text-gray-500">{label}</div>
      <div className={mono ? "font-mono text-xs" : ""}>{value ?? "—"}</div>
    </div>
  );
}
